/**
 * Generates a simple PDF receipt for offline orders (desktop app only).
 * Used when backend receipt API is unavailable. Creates a basic invoice layout.
 * Web/Android never use this - only called when window.electronAPI exists.
 */

import { jsPDF } from 'jspdf';

/**
 * Generate PDF receipt from offline order data.
 * @param {Object} params
 * @param {Array} params.cartItems - Cart items with product, quantity, price, etc.
 * @param {Object} params.customer - Customer object { name, phone, email }
 * @param {Object} params.totals - { subtotal, taxAmount, discount, total }
 * @param {string} params.localInvoiceNo - e.g. "OFF-0001"
 * @param {string} params.paymentMethod - e.g. "CASH"
 * @param {string} params.notes - Optional notes
 * @param {Object} params.company - Company info { name } from user
 * @returns {Uint8Array} PDF as bytes for electronAPI.printPdf
 */
export function generateOfflineReceiptPdf({
  cartItems = [],
  customer = null,
  totals = {},
  localInvoiceNo = 'OFF-0000',
  paymentMethod = 'CASH',
  notes = '',
  company = {}
}) {
  const doc = new jsPDF({ format: 'a4' });
  const pageWidth = doc.internal.pageSize.getWidth();
  let y = 20;

  const companyName = company?.name || 'Tiya Inventory';
  doc.setFontSize(18);
  doc.text(companyName, pageWidth / 2, y, { align: 'center' });
  y += 10;

  doc.setFontSize(12);
  doc.text('OFFLINE INVOICE / RECEIPT', pageWidth / 2, y, { align: 'center' });
  y += 8;

  doc.setFontSize(10);
  doc.text(`Invoice No: ${localInvoiceNo}`, 14, y);
  doc.text(`Date: ${new Date().toLocaleDateString()}`, pageWidth - 14, y, { align: 'right' });
  y += 10;

  if (customer) {
    doc.setFontSize(10);
    doc.text(`Customer: ${customer.name || 'Walk-in'}`, 14, y);
    y += 5;
    if (customer.phone) {
      doc.text(`Phone: ${customer.phone}`, 14, y);
      y += 5;
    }
  }
  y += 5;

  // Table header
  doc.setFont(undefined, 'bold');
  doc.text('Item', 14, y);
  doc.text('Qty', 80, y);
  doc.text('Price', 110, y);
  doc.text('Amount', 150, y);
  y += 6;
  doc.setFont(undefined, 'normal');

  doc.setDrawColor(0, 0, 0);
  doc.line(14, y, pageWidth - 14, y);
  y += 6;

  // Table rows
  for (const item of cartItems) {
    const productName = item.product?.name || 'Product';
    const qty = item.quantity || 0;
    const price = item.price || 0;
    const amount = (qty * price) - (item.discount || 0);
    doc.text(productName.substring(0, 35), 14, y);
    doc.text(String(qty), 80, y);
    doc.text(price.toFixed(2), 110, y);
    doc.text(amount.toFixed(2), 150, y);
    y += 6;
    if (y > 270) {
      doc.addPage();
      y = 20;
    }
  }

  y += 8;
  doc.line(14, y, pageWidth - 14, y);
  y += 6;

  doc.text('Subtotal:', 110, y);
  doc.text((totals.subtotal || 0).toFixed(2), 150, y);
  y += 6;
  if (totals.discount > 0) {
    doc.text('Discount:', 110, y);
    doc.text(`-${(totals.discount || 0).toFixed(2)}`, 150, y);
    y += 6;
  }
  if (totals.taxAmount > 0) {
    doc.text('Tax:', 110, y);
    doc.text((totals.taxAmount || 0).toFixed(2), 150, y);
    y += 6;
  }
  doc.setFont(undefined, 'bold');
  doc.text('Total:', 110, y);
  doc.text((totals.total || 0).toFixed(2), 150, y);
  doc.setFont(undefined, 'normal');
  y += 10;

  doc.text(`Payment: ${paymentMethod || 'CASH'}`, 14, y);
  y += 6;
  if (notes) {
    doc.text(`Notes: ${notes}`, 14, y);
    y += 6;
  }

  doc.setFontSize(8);
  doc.text('This is an offline receipt. Will sync when connection is restored.', pageWidth / 2, 285, { align: 'center' });

  const pdfBytes = doc.output('arraybuffer');
  return new Uint8Array(pdfBytes);
}
